import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDraggable, useDroppable } from "@dnd-kit/react";
import cx from "classnames";
import { DATE_FORMATS } from "../../../../../constants";
import { Header, FloatingHeader } from "./Header";
import Exercises from "./Exercises";
import SvgIcon from "../../../../../parts/SvgIcon";
import { PasteExerciseComponent } from "../../../../../parts/PasteExerciseComponent";
import { useMenstrualCycleDayInfo } from "../../menstrual-cycle/useMenstrualCycleDayInfo";

const { DATE_DAY_FORMAT } = DATE_FORMATS;

const DayNormal = ({
  isInActiveMonth,
  isPast,
  isToday,
  expanded,
  exercises,
  dayString,
  date,
  handleSelectDate,
  selectedDates,
  handleRemoveMultiple,
  supersets,
  withWeight,
  fromMetric,
  units,
  round,
  program,
  intensityMeasure,
  isRiR,
  mobile = false,
  removing,
  athlete,
  query,
  femaleCycleLogs,
  femaleCycleSettings,
  sections = [],
}) => {
  const { t } = useTranslation("athlete-portal");
  const { ref: refDroppable } = useDroppable({
    id: `drop-${dayString}`,
    disabled: !!exercises.length,
    data: {
      id: dayString,
    },
  });
  const {
    ref: refDraggable,
    handleRef: handleRefDraggable,
    isDragging,
  } = useDraggable({
    id: `drag-${dayString}`,
    data: {
      id: dayString,
    },
  });

  const [exercisesToCopy, setExercisesToCopy] = useState(null);
  const exercisesInClipboard = query?.exercisesInClipboard;
  /**
   * Check if there is an exercise storage and ready to be pasted
   */
  useEffect(() => {
    if (typeof window !== "undefined") {
      const exerciseInLocalStorage = localStorage.getItem("exercisesToCopy");
      setExercisesToCopy(
        exerciseInLocalStorage ? JSON.parse(exerciseInLocalStorage) : null
      );
    }
  }, [exercisesInClipboard]);

  const { menstrualCycleInfo, predictedCycleInfo } = useMenstrualCycleDayInfo(
    date,
    femaleCycleLogs,
    femaleCycleSettings
  );

  return (
    <div
      className={`day ${!isInActiveMonth ? " alt-other-month" : ""}${
        isPast ? " alt-past" : isToday ? " alt-today" : ""
      }${expanded ? " expanded" : ""}${
        !exercises.length && !sections.length ? " empty-workout-day" : ""
      } ${
        removing && !selectedDates.includes(dayString)
          ? " unselected"
          : removing && selectedDates.includes(dayString)
            ? " selected"
            : ""
      }`}
    >
      <div
        className={`day-margin${
          exercises.length ? " draggable-dropzone--occupied" : ""
        }${expanded ? " expanded" : ""}`}
        ref={refDroppable}
      >
        <Header
          date={date}
          mobile={mobile}
          program={program}
          handleSelectDate={handleSelectDate}
          selectedDates={selectedDates}
          handleRemoveMultiple={handleRemoveMultiple}
          dayString={dayString}
          removing={removing}
        />
        {exercises.length ? (
          <div
            className={cx(
              "day-draggable",
              isDragging ? "opacity-0" : "opacity-100"
            )}
          >
            <div ref={refDraggable}>
              {!mobile && (
                <FloatingHeader
                  handleSelectDate={handleSelectDate}
                  selectedDates={selectedDates}
                  handleRemoveMultiple={handleRemoveMultiple}
                  date={date}
                  dayString={dayString}
                  removing={removing}
                  exercises={exercises}
                  athlete={athlete}
                  handleRefDraggable={handleRefDraggable}
                  sections={sections}
                />
              )}
              <Exercises
                exercises={exercises}
                supersets={supersets}
                sections={sections}
                withWeight={withWeight}
                fromMetric={fromMetric}
                units={units}
                round={round}
                expanded={expanded}
                date={date}
                intensityMeasure={intensityMeasure}
                isRiR={isRiR}
                mobile={mobile}
                athlete={athlete}
              />
            </div>
          </div>
        ) : (
          <>
            {/* No exercises yet, but a section (e.g. "Game Day") was added to
                build the day around — keep it visible instead of hiding the
                day behind the add/paste affordance below. */}
            {sections.length > 0 && (
              <Exercises
                exercises={exercises}
                supersets={supersets}
                sections={sections}
                withWeight={withWeight}
                fromMetric={fromMetric}
                units={units}
                round={round}
                expanded={expanded}
                date={date}
                intensityMeasure={intensityMeasure}
                isRiR={isRiR}
                mobile={mobile}
                athlete={athlete}
              />
            )}
            {exercisesToCopy ? (
              <div
                className={`add-paste-exercise ${menstrualCycleInfo || predictedCycleInfo ? "menstrual-cycle-paste-exercise" : ""}`}
              >
                <AddExerciseButton date={date} classNames="add-paste" />
                <PasteExerciseComponent
                  athlete={athlete}
                  date={date.format(DATE_DAY_FORMAT)}
                  withFullInfo={true}
                  classNames="add-paste"
                  isProgram={false}
                  query={query?.exercisesInClipboard}
                />
              </div>
            ) : (
              <AddExerciseButton date={date} classNames="add" />
            )}
          </>
        )}
        {menstrualCycleInfo && (
          <div className="menstrual-cycle-indicator-wrapper">
            <a
              href={`/athlete-portal/metrics/female-cycle?date=${date.format("YYYYMMDD")}&athlete=${athlete?._id ?? ""}${query?.organizationCoach ? `&organizationCoach=${query.organizationCoach}` : ""}`}
              className="menstrual-cycle-indicator"
              onClick={(e) => e.stopPropagation()}
              style={{ backgroundColor: "#d7567a" }}
            >
              {t("dayNormal.menstrualCycleDay", {
                number: menstrualCycleInfo.dayNumber,
              })}
            </a>
          </div>
        )}
        {!menstrualCycleInfo && predictedCycleInfo && (
          <div className="menstrual-cycle-indicator-wrapper">
            <a
              href={`/athlete-portal/metrics/female-cycle?date=${date.format("YYYYMMDD")}&athlete=${athlete?._id ?? ""}${query?.organizationCoach ? `&organizationCoach=${query.organizationCoach}` : ""}`}
              className="menstrual-cycle-indicator menstrual-cycle-indicator--predicted"
              onClick={(e) => e.stopPropagation()}
            >
              {t("dayNormal.predictedCycleDay", {
                number: predictedCycleInfo.dayNumber,
              })}
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default DayNormal;

const AddExerciseButton = ({ date, classNames }) => {
  const { t } = useTranslation("athlete-portal");
  return (
    <a
      className={classNames}
      href={`/actions/refresh?modal[type]=calendar-add-exercise&day=${date.format(
        DATE_DAY_FORMAT
      )}`}
    >
      <SvgIcon
        src="/img/new-icons/add-square-outline.svg"
        className="h-11 w-11"
      />
      <p className="p4 text-center">{t("dayNormal.addExercise")}</p>
    </a>
  );
};
